import { useModalStore } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import { GamingHallLabel } from '@/entities/sccrs';
import { getCalculatedCommission } from '@/entities/operation';
import {
  ResultModal,
  WithdrawConfirmationModalContainer,
  WithdrawSccrsModal,
  type WithdrawSccrsModalState,
} from '@/features/withdraw-sccrs';

export function useWithdrawSccrsModals() {
  const { showDialogComponent, closeDialog, getState, cleanup, getAllState } =
    useModalStore();
  const { t } = useTranslation();

  const onCloseDialog = () => {
    closeDialog();
    cleanup();
  };

  const closeConfirmation = {
    title: t('withdrawMethods.crypto.exitConfirmTitle'),
    description: t('withdrawMethods.crypto.exitConfirmDescription'),
    confirmText: t('withdrawMethods.crypto.exitConfirmButton'),
    showCancelButton: false,
  };

  function getStoreData() {
    const storeData = getAllState<WithdrawSccrsModalState>();

    const commissionData = getCalculatedCommission({
      amount: +storeData?.totalAmount || 0,
      assetCode: storeData?.withdrawSccrsFormData?.currencyCode || '',
      includeFee: !!storeData?.withdrawSccrsFormData?.includeFee,
      fee: storeData?.fee || 0,
      precision: NUMERIC_CONFIG.precision.default,
    });

    return {
      commissionData,
      storeData,
    };
  }

  const openResultModal = ({ success }: { success: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <ResultModal
          success={success}
          data={{
            commissionData,
            platform: <GamingHallLabel hall={storeData.platform} />,
            fls: storeData.withdrawSccrsFormData.fls,
          }}
          onClose={() => {
            if (success) {
              onCloseDialog();
              return;
            }

            openWithdrawSccrsModal();
          }}
        />
      ),
      onClose: onCloseDialog,
    });
  };

  const openWithdrawSccrsModal = () => {
    const openOperationMethodsModal = getState(
      'onWithdrawFormBack'
    ) as () => void;

    const { storeData } = getStoreData();

    showDialogComponent({
      component: (
        <WithdrawSccrsModal
          title={t('withdrawMethods.sccrs.title')}
          initialData={storeData.withdrawSccrsFormData}
          onBack={() => {
            cleanup();
            openOperationMethodsModal();
          }}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: false,
    });
  };

  const openConfirmationModal = (params?: { continueMFA?: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <WithdrawConfirmationModalContainer
          onBack={openWithdrawSccrsModal}
          storeData={storeData}
          commissionData={commissionData}
          continueMFA={params?.continueMFA}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
    });
  };

  return {
    openWithdrawSccrsModal,
    openConfirmationModal,
    openResultModal,
  };
}
